import type { SlotDay } from '../../bookings/data/bookingData';
import { formatTime12h, timeToMinutes, isSlotInPast } from './servicesCalendarUtils';

export type ServiceTimeSlot = {
  id: string;
  startTime: string;
  endTime: string;
};

/** serviceId → date (YYYY-MM-DD) → slots */
export type ServiceAvailabilityMap = Record<string, Record<string, ServiceTimeSlot[]>>;

export const SERVICE_IDS = ['referral-session', 'resume-review', 'interview-mock'] as const;
export type ServiceId = (typeof SERVICE_IDS)[number];

/** Maps job-seeker booking flow types to referrer service ids */
export const BOOKING_TYPE_TO_SERVICE_ID: Record<string, ServiceId> = {
  'referral-session': 'referral-session',
  'resume-review': 'resume-review',
  'culture-call': 'interview-mock',
};

/** Maps referrer service ids → platform feature keys (for feature-flag filtering) */
export const SERVICE_ID_TO_FEATURE_KEY: Partial<Record<ServiceId, string>> = {
  'referral-session': 'referral-session',
  'resume-review': 'resume-review',
  'interview-mock': 'culture-call',
};

const DRAFT_KEY_PREFIX = 'connectx_services_availability_draft';
const PUBLISHED_KEY_PREFIX = 'connectx_services_availability_published';
const LEGACY_KEY = 'connectx_services_availability';

function draftKey(userId: string) {
  return `${DRAFT_KEY_PREFIX}_${userId}`;
}

function publishedKey(userId: string) {
  return `${PUBLISHED_KEY_PREFIX}_${userId}`;
}

function parseMap(raw: string | null): ServiceAvailabilityMap | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ServiceAvailabilityMap;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function emptyAvailabilityMap(): ServiceAvailabilityMap {
  return {};
}

export function loadDraftAvailability(userId: string): ServiceAvailabilityMap {
  const fromDraft = parseMap(localStorage.getItem(draftKey(userId)));
  if (fromDraft) return fromDraft;

  const legacy = parseMap(localStorage.getItem(LEGACY_KEY));
  if (legacy) {
    localStorage.setItem(draftKey(userId), JSON.stringify(legacy));
    return legacy;
  }

  return emptyAvailabilityMap();
}

export function loadPublishedAvailability(userId: string): ServiceAvailabilityMap {
  return parseMap(localStorage.getItem(publishedKey(userId))) ?? emptyAvailabilityMap();
}

export function saveDraftAvailability(userId: string, map: ServiceAvailabilityMap): void {
  localStorage.setItem(draftKey(userId), JSON.stringify(map));
}

export function savePublishedAvailability(userId: string, map: ServiceAvailabilityMap): void {
  localStorage.setItem(publishedKey(userId), JSON.stringify(map));
  localStorage.setItem(
    `${publishedKey(userId)}_updatedAt`,
    new Date().toISOString()
  );
}

export function publishDraft(userId: string, draft: ServiceAvailabilityMap): ServiceAvailabilityMap {
  const published = JSON.parse(JSON.stringify(draft)) as ServiceAvailabilityMap;
  savePublishedAvailability(userId, published);
  saveDraftAvailability(userId, draft);
  return published;
}

export function availabilityMapsEqual(
  a: ServiceAvailabilityMap,
  b: ServiceAvailabilityMap
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function countSlotsForService(map: ServiceAvailabilityMap, serviceId: string): number {
  const targetServiceId = (serviceId !== 'resume-review' && map['unified-availability'] !== undefined)
    ? 'unified-availability'
    : serviceId;
  const serviceData = map[targetServiceId] || map[serviceId] || {};
  return Object.values(serviceData).reduce((sum, slots) => sum + slots.length, 0);
}

function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  const dateObj = new Date(y, m - 1, d);
  return dateObj.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export type ActiveTimeSlotItem = {
  dateStr: string;
  dateLabel: string;
  timeLabel: string;
  startTime: string;
  endTime: string;
};

export function getActiveFreeTimeSlots(map: ServiceAvailabilityMap): ActiveTimeSlotItem[] {
  const unified = map['unified-availability'] || map['referral-session'] || map['interview-mock'] || {};
  const result: ActiveTimeSlotItem[] = [];
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const sortedDates = Object.keys(unified)
    .filter((dStr) => dStr >= todayStr)
    .sort((a, b) => a.localeCompare(b));

  for (const dateStr of sortedDates) {
    const slots = unified[dateStr] || [];
    const dateLabel = formatDateLabel(dateStr);

    const sortedSlots = [...slots].sort((x, y) => x.startTime.localeCompare(y.startTime));

    for (const slot of sortedSlots) {
      if (!isSlotInPast(dateStr, slot.endTime)) {
        result.push({
          dateStr,
          dateLabel,
          timeLabel: `${formatTime12h(slot.startTime)} – ${formatTime12h(slot.endTime)}`,
          startTime: slot.startTime,
          endTime: slot.endTime,
        });
      }
    }
  }

  return result;
}

/**
 * Splits a single availability window into individual bookable slots.
 * E.g., 09:00–11:00 with 30-min duration and 5-min buffer produces:
 *   09:00–09:30, 09:35–10:05, 10:10–10:40
 *
 * Returns an array of { startTime, endTime } pairs.
 */
export function splitWindowIntoSlots(
  windowStart: string,
  windowEnd: string,
  durationMinutes: number,
  bufferMinutes: number = 0
): { startTime: string; endTime: string }[] {
  const slots: { startTime: string; endTime: string }[] = [];
  const windowEndMins = timeToMinutes(windowEnd);
  let cursorMins = timeToMinutes(windowStart);

  while (cursorMins + durationMinutes <= windowEndMins) {
    const slotStart = `${String(Math.floor(cursorMins / 60)).padStart(2, '0')}:${String(cursorMins % 60).padStart(2, '0')}`;
    const endMins = cursorMins + durationMinutes;
    const slotEnd = `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`;
    slots.push({ startTime: slotStart, endTime: slotEnd });
    cursorMins = endMins + bufferMinutes;
  }

  return slots;
}

/**
 * Helper to check if a specific slot on a specific day overlaps with any booked session.
 * Date string is in YYYY-MM-DD format.
 * slotStart and slotEnd are in HH:MM (24-hour) format.
 */
export function isSlotOverlappingWithBookings(
  dateStr: string,
  slotStart: string,
  slotEnd: string,
  bookedRanges: Array<{ start: Date; end: Date }>
): boolean {
  if (!bookedRanges || bookedRanges.length === 0) return false;

  // Parse slot start and end into Date objects in the user's local timezone
  // dateStr is 'YYYY-MM-DD', slotStart is 'HH:MM'
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  const [startH, startM] = slotStart.split(':').map((n) => parseInt(n, 10));
  const [endH, endM] = slotEnd.split(':').map((n) => parseInt(n, 10));

  const slotStartDate = new Date(y, m - 1, d, startH, startM, 0, 0);
  const slotEndDate = new Date(y, m - 1, d, endH, endM, 0, 0);

  // Check overlap with any booked range
  return bookedRanges.some((booking) => {
    return slotStartDate < booking.end && booking.start < slotEndDate;
  });
}

/** Converts published referrer availability into booking slot pills for job seekers.
 *
 * When durationMinutes is provided, each availability window is auto-split
 * into individual bookable slots of that duration (with optional buffer).
 * When null/undefined, the raw availability windows are shown as-is.
 */

export function publishedAvailabilityToSlotDays(
  map: ServiceAvailabilityMap,
  serviceId: string,
  durationMinutes?: number | null,
  bufferMinutes?: number,
  bookedRanges?: Array<{ start: Date; end: Date }> | null
): SlotDay[] {
  const targetServiceId = (serviceId !== 'resume-review' && map['unified-availability'] !== undefined)
    ? 'unified-availability'
    : serviceId;
  const serviceData = map[targetServiceId] || map[serviceId] || {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nowY = now.getFullYear();
  const nowM = String(now.getMonth() + 1).padStart(2, '0');
  const nowD = String(now.getDate()).padStart(2, '0');
  const nowDateStr = `${nowY}-${nowM}-${nowD}`;
  const buf = bufferMinutes ?? 0;

  return Object.entries(serviceData)
    .filter(([dateStr]) => {
      const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
      const slotDate = new Date(y, m - 1, d);
      return slotDate >= today;
    })
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateStr, windows]) => {
      const sortedWindows = [...windows].sort((x, y) => x.startTime.localeCompare(y.startTime));

      let slotLabels: string[];

      if (durationMinutes && durationMinutes > 0) {
        // Auto-split each window into individual bookable slots
        slotLabels = sortedWindows.flatMap((window) => {
          let splits = splitWindowIntoSlots(window.startTime, window.endTime, durationMinutes, buf);

          // If date is today, filter out past slots
          if (dateStr === nowDateStr) {
            splits = splits.filter((s) => {
              const [sh, sm] = s.startTime.split(':').map((n) => parseInt(n, 10));
              const slotStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sh, sm, 0);
              // Give 5-minute buffer from current time
              return slotStart.getTime() > (now.getTime() - 5 * 60 * 1000);
            });
          }

          // Filter out slots that overlap with bookings
          const nonOverlappingSplits = bookedRanges && bookedRanges.length > 0
            ? splits.filter(s => !isSlotOverlappingWithBookings(dateStr, s.startTime, s.endTime, bookedRanges))
            : splits;

          return nonOverlappingSplits.map(
            (s) => `${formatTime12h(s.startTime)} – ${formatTime12h(s.endTime)}`
          );
        });
      } else {
        // Show raw windows as-is (legacy behavior)
        let windowsToDisplay = sortedWindows;
        if (dateStr === nowDateStr) {
          windowsToDisplay = sortedWindows.filter((slot) => {
            const [eh, em] = slot.endTime.split(':').map((n) => parseInt(n, 10));
            const slotEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eh, em, 0);
            return slotEnd.getTime() > now.getTime();
          });
        }

        slotLabels = windowsToDisplay.map(
          (slot) => `${formatTime12h(slot.startTime)} – ${formatTime12h(slot.endTime)}`
        );
      }

      return {
        day: formatDateLabel(dateStr),
        slots: slotLabels,
      };
    })
    .filter((day) => day.slots.length > 0);
}

/** Finds the first published map in this browser that has slots for the service */
function findAnyPublishedMapWithService(serviceId: string): ServiceAvailabilityMap | null {
  if (typeof localStorage === 'undefined') return null;

  const keys = Object.keys(localStorage).filter((key) => key.startsWith(`${PUBLISHED_KEY_PREFIX}_`));

  for (const key of keys) {
    const map = parseMap(localStorage.getItem(key));
    if (map && countSlotsForService(map, serviceId) > 0) {
      return map;
    }
  }

  return null;
}

/** Load published slots for a job-seeker booking type */
export function getPublishedSlotsForBooking(bookingType: string, userId?: string): SlotDay[] {
  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[bookingType];
  if (!serviceId) return [];

  if (userId) {
    const published = loadPublishedAvailability(userId);
    const days = publishedAvailabilityToSlotDays(published, serviceId);
    if (days.length > 0) return days;
  }

  const anyPublished = findAnyPublishedMapWithService(serviceId);
  if (anyPublished) {
    return publishedAvailabilityToSlotDays(anyPublished, serviceId);
  }

  return publishedAvailabilityToSlotDays(loadPublishedAvailability('local'), serviceId);
}

/** Published map for a specific referrer (falls back to local demo user) */
export function getPublishedSlotsForReferrer(
  referrerUserId: string,
  serviceId: ServiceId
): SlotDay[] {
  const published = loadPublishedAvailability(referrerUserId);
  return publishedAvailabilityToSlotDays(published, serviceId);
}

// ── DB ↔ Map converters ─────────────────────────────────

export interface ServiceConfigMeta {
  referral_call_duration: number | null;
  price: number | null;
  is_active: boolean;
}

/** Default prices for each service */
export const DEFAULT_SERVICE_PRICES: Record<string, number> = {
  'resume-review': 299,
  'referral-session': 500,
  'interview-mock': 800,
};

/** Default call duration for referral sessions */
export const DEFAULT_REFERRAL_CALL_DURATION = 20;

/** Convert DB rows → ServiceAvailabilityMap + per-service meta */
export function buildFromDbRows(
  rows: Array<{
    service_id: string;
    availability: Record<string, { id: string; startTime: string; endTime: string }[]>;
    referral_call_duration?: number | null;
    price?: number | null;
    is_active?: boolean;
  }>
): { map: ServiceAvailabilityMap; meta: Record<string, ServiceConfigMeta> } {
  const map: ServiceAvailabilityMap = {};
  const meta: Record<string, ServiceConfigMeta> = {};

  for (const row of rows) {
    map[row.service_id] = row.availability || {};
    meta[row.service_id] = {
      referral_call_duration: row.referral_call_duration ?? null,
      price: row.price ?? null,
      is_active: row.is_active ?? true,
    };
  }

  return { map, meta };
}
